import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import * as moment from 'moment';
import { GroupedReminder, PharmacyReminder } from '../models/reminder.model';

@Injectable({ providedIn: 'root' })

export class ReminderService {
    
    reminderSubject$ = new BehaviorSubject<GroupedReminder[]>([]);
    reminder$ = this.reminderSubject$.asObservable();

    private openSnoozerSubject = new BehaviorSubject<boolean>(false);
    isOpenSnoozer$ = this.openSnoozerSubject.asObservable();

    constructor() {}

    processReminders(data: PharmacyReminder[]) {
        const groupedMap = new Map<string, GroupedReminder>();
        const currentReminders = this.reminderSubject$.getValue();
        const now = moment();

        const validVendors = new Set(data.map(d => d.ReminderEntityName.toLowerCase())); 
        for (const entry of data) {
            const baseTime = moment(entry.ReminderTime, 'HH:mm:ss');

            if (!baseTime.isSame(now, 'day')) continue;

            const offsets = entry.RemindBefore.split(',').map(m => parseInt(m.trim(), 10));
            for (const mins of offsets) {
                const reminderMoment = moment(baseTime).subtract(mins, 'minutes');
                const timeKey = reminderMoment.format('HH:mm');
                const remindBefore = moment.utc().startOf('day').add(mins, 'minutes').format('HH:mm');
                const vendorName = entry.ReminderEntityName;
                const key = `${timeKey}|${vendorName}`;

                if (!groupedMap.has(key)) {
                    groupedMap.set(key, {
                        index: 0,
                        time: timeKey,
                        ReminderTime: baseTime.format('HH:mm'),
                        RemindBefore: [remindBefore],
                        vendors: [vendorName],
                        isDismissed: false,
                        isSnoozedVal: null
                    });
                }
            }
        }

        for (const reminder of currentReminders) {
            for (const vendor of reminder.vendors) {
                const key = `${reminder.time}|${vendor}`;
                const reminderTime = moment(reminder.time, 'HH:mm');
                const isFutureSnooze = reminder.isSnoozedVal && reminderTime.isAfter(now);
                const isVendorStillValid = validVendors.has(vendor.toLowerCase());
                if (isFutureSnooze && isVendorStillValid && !groupedMap.has(key)) {
                    groupedMap.set(key, {
                        ...reminder,
                        vendors: [vendor]
                    });
                }
            }
        }

        const result: GroupedReminder[] = Array.from(groupedMap.values()).map((r, idx) => ({
            ...r,
            index: idx
        }));

        this.reminderSubject$.next(result);
        this.openSnoozerSubject.next(true);
    }

    snoozeReminder(
            group: GroupedReminder,
            vendor: string,
            originalOffsetMins: number,
            selectedSnooze: number
        ): string | null {
            const current = this.reminderSubject$.getValue();
            const now = moment();
            let updatedOffset: string | null = null;

            const updatedGroups: GroupedReminder[] = [];

            for (const g of current) {
                const isSameTime = g.time === group.time;
                const hasVendor = g.vendors.includes(vendor);
                if (isSameTime && hasVendor) {
                    // Keep ReminderTime unchanged
                    const originalReminderTime = g.ReminderTime;

                    const cutoffTime = moment(originalReminderTime, 'HH:mm');
                    const snoozedTime = moment(now).add(selectedSnooze, 'minutes');

                    updatedOffset = snoozedTime.format("HH:mm");
                    updatedGroups.push({
                        ...g,
                        time: updatedOffset,                 // only update time
                        ReminderTime: originalReminderTime,  // keep original reminder time
                        RemindBefore: ['00:00'],
                        isSnoozedVal: selectedSnooze,
                        vendors: [vendor],
                        isDismissed: false
                    });
                } else {
                    updatedGroups.push(g);
                }
            }

        this.reminderSubject$.next(updatedGroups);
        return updatedOffset;
    }

    dismissVendor(group: GroupedReminder, vendor: any) {
        const updated = this.reminderSubject$.getValue().map(g => {
            if (g.time === group.time && g.vendors.includes(vendor)) {
                return {
                    ...g,
                    vendors: g.vendors.filter(v => v !== vendor)
                };
            }
            return g;
        }).filter(g => g.vendors.length > 0);
        this.reminderSubject$.next(updated);
    }

    dismissAll() {
        this.reminderSubject$.next([]);
    }
}
